import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { addOnOptions } from '../data/mockData';
import { Lightbulb, Shield, Cloud, Activity } from 'phosphor-react';

const iconMap = {
  lightbulb: Lightbulb,
  shield: Shield,
  cloud: Cloud,
  activity: Activity
};

export const AddOnsSelector = ({ selected, onSelect }) => {
  const handleToggleAddOn = (addOnId) => {
    const newSelected = selected.includes(addOnId)
      ? selected.filter(id => id !== addOnId)
      : [...selected, addOnId];
    onSelect(newSelected);
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-slate-100 mb-2">Additional Features</h3>
        <p className="text-sm text-slate-400">Select optional features and enhancements</p>
      </div>

      <div className="space-y-3">
        {addOnOptions.map((addon) => {
          const IconComponent = iconMap[addon.icon];
          const isSelected = selected.includes(addon.id);
          
          return (
            <Card 
              key={addon.id}
              className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                isSelected 
                  ? 'ring-2 ring-blue-500 bg-slate-700 border-blue-500' 
                  : 'border-slate-600 bg-slate-750 hover:border-blue-400 hover:bg-slate-700'
              }`}
              onClick={() => handleToggleAddOn(addon.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleToggleAddOn(addon.id)}
                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 bg-slate-600 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-200">{addon.name}</h4>
                      <p className="text-sm text-slate-400">{addon.description}</p>
                    </div>
                  </div>
                  
                  {isSelected && (
                    <Badge className="bg-emerald-600 text-white">Added</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Selected Summary */}
      {selected.length > 0 && (
        <Card className="bg-emerald-900/20 border-emerald-600/30">
          <CardContent className="p-4">
            <h4 className="font-semibold text-emerald-400 mb-2">Selected Add-Ons ({selected.length})</h4>
            <div className="flex flex-wrap gap-2">
              {selected.map((addonId) => {
                const addon = addOnOptions.find(a => a.id === addonId);
                return addon ? (
                  <Badge key={addonId} className="bg-emerald-600 text-white text-xs">
                    {addon.name}
                  </Badge>
                ) : null;
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selection Actions */}
      <div className="pt-4 border-t border-slate-600">
        <div className="flex gap-2">
          <Button 
            variant="outline"
            className="flex-1 border-slate-600 bg-slate-700 text-slate-200 hover:bg-slate-600"
            onClick={() => onSelect([])}
            disabled={selected.length === 0}
          >
            Clear All
          </Button>
          <Button 
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => onSelect(addOnOptions.map(a => a.id))}
            disabled={selected.length === addOnOptions.length}
          >
            Select All
          </Button>
        </div>
      </div>
    </div>
  );
};